import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DeletedBaseType, IncidentApiService, IncidentResponse, IncidentTransaction, TransactionApiService} from '@npck/af-frontend-api';
import {LocalizationService, NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, filter, mergeMap} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../../../components/delete-dialog/delete-dialog.component';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {INCIDENT_ID, INCIDENT_TYPE_CODE, REFUND_ID, TRANSACTION_ID, TRANSACTION_INDEX} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {IncidentDetailsTab} from '../../incident-details.component';

@Component({
    selector: 'af-transaction-details',
    standalone: false,
    templateUrl: './transaction-details.component.html',
})
export class TransactionDetailsComponent implements OnInit  {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    Path = Path;
    tabPathToTab: Record<string, IncidentDetailsTab> = {[Path.TRANSACTIONS]: 'tabTransactions'};
    incidentId: string;
    refundId: string;
    transactionId: string;
    transactionIndex: number;
    transaction: IncidentTransaction;
    incident!: IncidentResponse;


    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private transactionApiService: TransactionApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private routerService: RouterService,
        private incidentApiService: IncidentApiService,
        private snackBarService: SnackbarService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.params[INCIDENT_ID]
            ?? this.activatedRoute.snapshot.queryParams[INCIDENT_ID];
        this.refundId = this.activatedRoute.snapshot.params[REFUND_ID];
        this.transactionId = this.activatedRoute.snapshot.params[TRANSACTION_ID];
        this.transactionIndex = +this.activatedRoute.snapshot.queryParams[TRANSACTION_INDEX];

        this.transactionApiService.getIncidentTransaction(this.incidentId, this.transactionId)
            .pipe(rxSpinner('initial'))
            .subscribe(response => this.transaction = response);
    }

    get canEditTransaction(): boolean {
        if (this.incident?.isInnocentStatus) {
            return false;
        }
        if (this.authHolderService.hasAccess(['government-user']) && this.transaction?.statusCode !== 'C') {
            return false;
        }
        return this.transaction?.isEditable !== false;
    }

    ngOnInit(): void {
        this.loadIncident();
    }

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }

        if (this.refundId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#RefundId', {[REFUND_ID]: this.refundId});
        }

        if (this.transactionId === path) {
            return this.localizationService
                .getLocalizedKey('i18n_af_labelIncidentTransactionIndex', {index: (this.transactionIndex + 1).toString()});
        }
        return path;
    };

    deleteTransaction(): void {
        this.dialogService.openCustomDialog<DeleteDialogComponent, {comment: string; reason: DeletedBaseType | null}>(
            DeleteDialogComponent,
            {
                id: this.transactionId,
                title: 'i18n_af_incidentTransactionDeletionDialogTitle',
                subtitle: 'i18n_af_incidentTransactionDeletionDialogSubtitle',
                showReason: true,
                reasonFilter: ['incident', 'all'],
            } as DeleteDialogData,
        )
            .pipe(
                rxSpinner('delete'),
                filter(response => !!response),
                mergeMap(result => {
                    if (!result) {
                        return EMPTY;
                    }
                    const normalized: {comment: string; reason: DeletedBaseType | null} =
                        typeof result === 'string' ? {comment: result, reason: null} : result;

                    return this.transactionApiService.deleteIncidentTransaction(
                        this.incidentId,
                        this.transactionId,
                        normalized.reason,
                        normalized.comment
                    );
                }),
            )
            .subscribe(() => {
                this.snackBarService.showSnackBar(
                    'i18n_af_snackBarMessageIncidentTransactionDeleted',
                    'success',
                    false, {transactionId: this.transactionId}
                );
                this.mainContainer.breadcrumbs.navigateBack();
            });
    }

    editTransaction(): void {
        this.routerService.navigate(Path.INCIDENTS_TRANSACTIONS_EDIT, {
                pathParams: {
                    [INCIDENT_ID]: this.incidentId,
                    [TRANSACTION_ID]: this.transactionId,
                },
                queryParams: {
                    [NESTED_TAB]: 'tabTransactions',
                    [TRANSACTION_INDEX]: this.transactionIndex,
                    [INCIDENT_TYPE_CODE]: this.incident.typeCode,
                },
            },
        );
    }

    private loadIncident(): void {
        this.incidentApiService.getIncident(this.incidentId)
            .subscribe(response => {
                this.incident = response;
            });
    }
}
